<template>
  <div class="min-h-screen lg:flex">
    <Sidebar />
    <GlobalNavbar />
    <main class="min-w-0 flex-1 px-6 pb-12 pt-28 lg:ml-64 lg:px-12">
      <div v-if="manual" class="relative mx-auto max-w-4xl">
        <aside v-if="manual.sections.length" class="fixed right-8 top-32 z-10 hidden w-52 xl:block">
          <p class="text-xs font-semibold uppercase tracking-wide text-zinc-400">Neste manual</p>
          <nav class="mt-3 border-l border-zinc-200" aria-label="Navegação do manual">
            <a
              v-for="(section, index) in manual.sections"
              :key="section.title"
              :href="`#${sectionId(index, section.title)}`"
              :class="['-ml-px block border-l-2 px-3 py-2 text-xs leading-5 transition', activeSection === sectionId(index, section.title) ? 'border-blue-600 font-medium text-zinc-950' : 'border-transparent text-zinc-500 hover:border-zinc-300 hover:text-zinc-900']"
              @click="scrollToSection($event, sectionId(index, section.title))"
            >
              {{ section.title }}
            </a>
          </nav>
        </aside>
        <div class="flex flex-wrap items-center gap-2 text-sm text-zinc-500">
          <Link href="/manuais" class="font-medium hover:text-zinc-950">Manuais</Link>
          <span aria-hidden="true">/</span>
          <Link v-if="solution" :href="`/manuais/categoria/${getManualCategory(manual)}`" class="font-medium hover:text-zinc-950">{{ categoryName }}</Link>
          <span aria-hidden="true">/</span>
          <span class="truncate text-zinc-400">{{ manual.title }}</span>
        </div>
        <div class="mt-10 flex flex-wrap items-center gap-3">
          <p class="text-sm font-semibold text-blue-600">Manual do produto</p>
          <Link v-if="manual" :href="`/manuais/categoria/${getManualCategory(manual)}`" class="rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-200">{{ categoryName }}</Link>
        </div>
        <div class="mt-3 flex items-start gap-3 lg:relative">
          <button
            type="button"
            class="order-2 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-200 text-zinc-400 transition hover:border-zinc-400 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 lg:absolute lg:-left-12 lg:top-1/2 lg:order-none lg:-translate-y-1/2"
            :aria-label="copiedLink ? 'Link copiado' : 'Copiar link do manual'"
            :title="copiedLink ? 'Link copiado' : 'Copiar link do manual'"
            @click="copyLink"
          >
            <PhCheck v-if="copiedLink" class="h-4 w-4 text-emerald-600" weight="bold" aria-hidden="true" />
            <PhCopy v-else class="h-4 w-4" weight="duotone" aria-hidden="true" />
          </button>
          <h1 class="min-w-0 flex-1 text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl">{{ manual.title }}</h1>
        </div>
        <p class="mt-4 max-w-2xl text-lg leading-8 text-zinc-600">{{ manual.description }}</p>
        <div v-if="manual.videoUrl" class="mt-8 overflow-hidden rounded-xl border border-zinc-200 bg-zinc-100 shadow-sm">
          <div class="aspect-video w-full">
            <iframe
              :src="manual.videoUrl"
              :title="`Vídeo: ${manual.title}`"
              class="h-full w-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            />
          </div>
        </div>
        <div class="mt-12 space-y-12">
          <section v-for="(section, index) in manual.sections" :id="sectionId(index, section.title)" :key="section.title" class="scroll-mt-28 border-t border-zinc-200 pt-8">
            <p class="text-sm font-semibold text-zinc-400">{{ String(index + 1).padStart(2, "0") }}</p>
            <h2 class="mt-2 text-2xl font-semibold text-zinc-950">{{ section.title }}</h2>
            <p v-if="section.description" class="mt-2 text-sm leading-6 text-zinc-600" v-html="linkify(section.description)"></p>
            <ol class="mt-6 space-y-4">
              <li v-for="(step, stepIndex) in section.steps" :key="step" class="flex gap-4 text-sm leading-6 text-zinc-700">
                <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-xs font-semibold text-zinc-600">{{ stepIndex + 1 }}</span>
                <span v-html="linkify(step)"></span>
              </li>
            </ol>
          </section>
        </div>
        <section v-if="manual.notes?.length" class="mt-14 space-y-4">
          <h2 class="text-2xl font-semibold text-zinc-950">Dicas e solução de problemas</h2>
          <div v-for="note in manual.notes" :key="note.title" :class="['rounded-lg border p-5', note.tone === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50']">
            <h3 class="font-semibold text-zinc-950">{{ note.title }}</h3>
            <p class="mt-2 text-sm leading-6 text-zinc-700" v-html="linkify(note.content)"></p>
          </div>
        </section>
        <section v-if="manual.commands?.length" class="mt-14">
          <h2 class="text-2xl font-semibold text-zinc-950">Comandos úteis</h2>
          <div class="mt-5 space-y-4">
            <div v-for="item in manual.commands" :key="item.command" class="rounded-lg border border-zinc-200 p-4">
              <div class="relative">
                <pre :class="['m-0 block max-w-full overflow-x-auto rounded-md px-3 py-2 pr-12 text-sm leading-6 whitespace-pre-wrap break-words', item.danger ? 'bg-red-950 text-red-100' : 'bg-zinc-950 text-zinc-100']"><code>{{ item.command }}</code></pre>
                <button
                  type="button"
                  class="absolute right-1 top-1 rounded-md p-2 text-zinc-400 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                  :aria-label="copiedCommand === item.command ? 'Copiado' : 'Copiar comando'"
                  @click="copyCommand(item.command)"
                >
                  <svg v-if="copiedCommand !== item.command" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect width="13" height="13" x="9" y="9" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                  <svg v-else class="h-4 w-4 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
                </button>
              </div>
              <p class="mt-3 text-sm leading-6 text-zinc-600" v-html="linkify(item.description)"></p>
            </div>
          </div>
        </section>
        <section class="mt-14 border-t border-zinc-200 pt-8">
          <div class="flex flex-wrap items-center justify-between gap-3">
            <Link v-if="previousManual" :href="`/manuais/${previousManual.slug}`" class="group max-w-[45%] text-sm text-zinc-500 hover:text-zinc-950">
              <span class="block text-xs text-zinc-400">← Manual anterior</span>
              <span class="mt-1 block truncate font-medium">{{ previousManual.title }}</span>
            </Link>
            <Link v-if="nextManual" :href="`/manuais/${nextManual.slug}`" class="group ml-auto max-w-[45%] text-right text-sm text-zinc-500 hover:text-zinc-950">
              <span class="block text-xs text-zinc-400">Próximo manual →</span>
              <span class="mt-1 block truncate font-medium">{{ nextManual.title }}</span>
            </Link>
          </div>
          <div v-if="relatedManuals.length" class="mt-10">
            <h2 class="text-lg font-semibold text-zinc-950">Manuais relacionados</h2>
            <div class="mt-4 grid gap-3 sm:grid-cols-2">
              <Link v-for="related in relatedManuals" :key="related.slug" :href="`/manuais/${related.slug}`" class="rounded-lg border border-zinc-200 p-4 transition hover:border-zinc-400">
                <span class="text-sm font-medium text-zinc-950">{{ related.title }}</span>
                <span class="mt-1 block text-xs leading-5 text-zinc-500">{{ related.description }}</span>
              </Link>
            </div>
          </div>
        </section>
      </div>
      <div v-else class="mx-auto max-w-2xl py-16"><h1 class="text-2xl font-semibold">Manual não encontrado</h1><Link href="/manuais" class="mt-4 inline-block text-sm text-blue-600">Voltar aos manuais</Link></div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { Link } from "@inertiajs/vue3";
import { onBeforeUnmount, onMounted, ref } from "vue";
import { PhCheck, PhCopy } from "@phosphor-icons/vue";
import Sidebar from "../components/layout/Sidebar.vue";
import GlobalNavbar from "../components/layout/GlobalNavbar.vue";
import { getManualCategory, manualCategories, manualCatalog } from "../data/manualCatalog";

const props = defineProps<{ slug: string }>();
const manual = manualCatalog.flatMap((solution) => solution.manuals).find((item) => item.slug === props.slug);
const categoryName = manualCategories.find((category) => category.slug === (manual ? getManualCategory(manual) : ""))?.name;
const solution = manualCatalog.find((item) => item.manuals.some((itemManual) => itemManual.slug === props.slug));
const solutionManuals = solution?.manuals || [];
const currentIndex = solutionManuals.findIndex((item) => item.slug === props.slug);
const previousManual = currentIndex > 0 ? solutionManuals[currentIndex - 1] : undefined;
const nextManual = currentIndex >= 0 ? solutionManuals[currentIndex + 1] : undefined;
const relatedManuals = solutionManuals.filter((item) => item.slug !== props.slug).slice(0, 4);
const activeSection = ref("");
const sectionId = (index: number, title: string) => `manual-section-${index}-${title.toLowerCase().replace(/[^a-z0-9áàâãéêíóôõúç]+/gi, "-").replace(/^-|-$/g, "")}`;
let sectionObserver: IntersectionObserver | undefined;
const scrollToSection = (event: MouseEvent, id: string) => {
  event.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  window.history.replaceState(null, "", `#${id}`);
};
const linkify = (text: string) => text
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\"/g, "&quot;")
  .replace(/\b(GET|POST|PATCH|DELETE) (\/v1\/[^\s<.,]+)/g, (_, method: string, path: string) => {
    const anchor = `${method}-${path}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    return `<a href="/stackzap#${anchor}" class="font-medium text-blue-600 underline decoration-blue-200 underline-offset-2 hover:text-blue-800">${method} ${path}</a>`;
  })
  .replace(/(https?:\/\/[^\s<]+)/g, (_, url: string) => {
    const trailing = url.match(/[.,!?;:)]+$/)?.[0] || "";
    const cleanUrl = trailing ? url.slice(0, -trailing.length) : url;
    return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="font-medium text-blue-600 underline decoration-blue-200 underline-offset-2 hover:text-blue-800">${cleanUrl}</a>${trailing}`;
  })
  .replace(/(\/manuais\/[a-z0-9-]+|\/stackzap(?:#[a-z0-9-]+)?)/g, '<a href="$1" class="font-medium text-blue-600 underline decoration-blue-200 underline-offset-2 hover:text-blue-800">$1</a>');
const copiedCommand = ref<string | null>(null);
const copiedLink = ref(false);
let copyTimeout: ReturnType<typeof setTimeout> | undefined;
let linkTimeout: ReturnType<typeof setTimeout> | undefined;
const copyLink = async () => {
  await navigator.clipboard.writeText(window.location.href);
  copiedLink.value = true;
  clearTimeout(linkTimeout);
  linkTimeout = setTimeout(() => { copiedLink.value = false; }, 2000);
};
const copyCommand = async (command: string) => {
  await navigator.clipboard.writeText(command);
  copiedCommand.value = command;
  clearTimeout(copyTimeout);
  copyTimeout = setTimeout(() => { copiedCommand.value = null; }, 2000);
};
onMounted(() => {
  if (!manual) return;
  const sections = manual.sections.map((section, index) => document.getElementById(sectionId(index, section.title))).filter(Boolean) as HTMLElement[];
  sectionObserver = new IntersectionObserver((entries) => {
    const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
    if (visible) activeSection.value = visible.target.id;
  }, { rootMargin: "-18% 0px -68%", threshold: 0 });
  sections.forEach((section) => sectionObserver?.observe(section));
  activeSection.value = sections[0]?.id || "";
});
onBeforeUnmount(() => sectionObserver?.disconnect());
</script>
